import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateChatCustomization, resetCustomization } from '../../reducers/chatSlice';

const presetThemes = [
  { name: 'Ocean', sender: '#0084FF', receiver: '#E3F2FD', bg: '#F0F4F8' },
  { name: 'Purple', sender: '#7B1FA2', receiver: '#F3E5F5', bg: '#F9F5FF' },
  { name: 'Rose', sender: '#E91E63', receiver: '#FCE4EC', bg: '#FFF5F7' },
  { name: 'Sunset', sender: '#F57C00', receiver: '#FFF3E0', bg: '#FFFBF5' },
  { name: 'Mint', sender: '#00BFA5', receiver: '#E0F2F1', bg: '#F1F8F6', receiverText: '#000000' },
  { name: 'Classic', sender: '#DCF8C6', receiver: '#FFFFFF', bg: '#ECE5DD' },
  { name: 'Lavender', sender: '#9575CD', receiver: '#EDE7F6', bg: '#FAF7FF', receiverText: '#000000' },
  { name: 'Peach', sender: '#FF7043', receiver: '#FFF0E0', bg: '#FFF8F5', receiverText: '#000000' },
  { name: 'Skyline', sender: '#42A5F5', receiver: '#E3F2FD', bg: '#F6FBFF' }
];

const CustomizationPanel = ({ isOpen, onClose, roomId }) => {
  const dispatch = useDispatch();
  const { chatCustomization } = useSelector(state => state.chat.ui);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => (document.body.style.overflow = 'unset');
  }, [isOpen]);

  const getTickColor = senderColor => {
    const lightColors = ['#DCF8C6', '#FFFFFF', '#E3F2FD', '#F3E5F5', '#FCE4EC', '#FFF3E0'];
    return lightColors.includes(senderColor) ? '#0088CC' : '#FFFFFF';
  };

  const handleThemeSelect = theme => {
    const senderTextColor = ['#DCF8C6', '#FFFFFF', '#E3F2FD', '#F3E5F5', '#FCE4EC', '#FFF3E0'].includes(theme.sender) ? '#000000' : '#FFFFFF';
    const receiverTextColor = theme.receiverText ? theme.receiverText : ['#FFFFFF', '#E3F2FD', '#F3E5F5', '#FCE4EC', '#FFF3E0'].includes(theme.receiver) ? '#000000' : '#FFFFFF';

    const newCustomization = {
      senderBubbleColor: theme.sender,
      receiverBubbleColor: theme.receiver,
      backgroundColor: theme.bg,
      senderTextColor,
      receiverTextColor,
      tickColor: getTickColor(theme.sender),
      textSize: chatCustomization.textSize || 15,
      messageGap: chatCustomization.messageGap || 6
    };

    dispatch(updateChatCustomization(newCustomization));

    if (roomId) {
      localStorage.setItem(`chatCustomization_${roomId}`, JSON.stringify(newCustomization));
      console.log(`🎨 [CustomizationPanel] Theme saved to localStorage for room "${roomId}":`, newCustomization);
    }
  };

  const handleReset = () => {
    const defaultTheme = presetThemes.find(t => t.name === 'Ocean');
    if (defaultTheme) {
      handleThemeSelect(defaultTheme);
    }
    if (roomId) {
      localStorage.removeItem(`chatCustomization_${roomId}`);
      console.log(`🎨 [CustomizationPanel] Reset stored theme for room "${roomId}"`);
    }
  };

  const panelContent = (
    <>
      <div className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'}`} onClick={onClose} />

      <div
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 z-50 max-w-md mx-auto ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ height: '55vh' }}
      >
        <div className="flex flex-col h-full">
          <div className="flex-shrink-0">
            <div className="flex items-center justify-between px-4 py-2">
              <h3 className="text-lg font-bold">Chat Theme</h3>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3">
            <div className="grid grid-cols-3 gap-3 px-1">
              {presetThemes.map((theme, index) => (
                <button
                  key={index}
                  onClick={() => handleThemeSelect(theme)}
                  className="relative rounded-xl overflow-hidden border-2 border-gray-200 hover:border-blue-400 hover:shadow-md transition-all active:scale-95 bg-white"
                >
                  <div className="aspect-square flex flex-col justify-between p-2.5 rounded-lg" style={{ backgroundColor: theme.bg }}>
                    <div className="flex justify-end">
                      <div
                        className="px-2.5 py-1.5 rounded-lg shadow-sm"
                        style={{
                          backgroundColor: theme.sender,
                          borderRadius: '10px 10px 3px 10px',
                          minWidth: '38px'
                        }}
                      >
                        <div className="w-6 h-1.5 bg-black bg-opacity-20 rounded"></div>
                      </div>
                    </div>

                    <div className="flex justify-start mt-2">
                      <div
                        className="px-2.5 py-1.5 rounded-lg shadow-sm"
                        style={{
                          backgroundColor: theme.receiver,
                          borderRadius: '10px 10px 10px 3px',
                          minWidth: '45px'
                        }}
                      >
                        <div className="w-8 h-1.5 bg-black bg-opacity-15 rounded"></div>
                      </div>
                    </div>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm py-1.5 border-t border-gray-100">
                    <p className="text-[11px] font-semibold text-gray-700 text-center tracking-wide">{theme.name}</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-3 p-3 bg-blue-50 rounded-xl">
              <p className="text-[10px] font-medium text-gray-600 mb-2">Preview</p>
              <div className="rounded-lg p-2 space-y-1.5" style={{ backgroundColor: chatCustomization.backgroundColor }}>
                <div className="flex justify-end items-end gap-1.5">
                  {/* Metadata */}
                  <div className="flex items-center gap-1 select-none mb-0.5 min-w-fit">
                    <span className="text-[9px] text-gray-400 font-medium leading-none">
                      12:30
                    </span>
                    <svg width="12" height="9" viewBox="0 0 16 11" fill="none">
                      <path d="M11.071 0.5L5.91 5.661L4.339 4.089L3.25 5.178L5.91 7.839L12.161 1.589L11.071 0.5Z" fill={chatCustomization.tickColor} />
                      <path d="M15.589 1.589L14.5 0.5L8.339 6.661L9.429 7.75L15.589 1.589Z" fill={chatCustomization.tickColor} />
                    </svg>
                  </div>
                  {/* Bubble */}
                  <div
                    className="rounded-xl px-2.5 py-1.5 shadow-sm"
                    style={{
                      backgroundColor: chatCustomization.senderBubbleColor,
                      borderRadius: '12px 12px 2px 12px'
                    }}
                  >
                    <p className="text-[11px] leading-normal" style={{ color: chatCustomization.senderTextColor }}>
                      Your message
                    </p>
                  </div>
                </div>
                <div className="flex justify-start items-end gap-1.5">
                  {/* Bubble */}
                  <div
                    className="rounded-xl px-2.5 py-1.5 shadow-sm"
                    style={{
                      backgroundColor: chatCustomization.receiverBubbleColor,
                      borderRadius: '12px 12px 12px 2px'
                    }}
                  >
                    <p className="text-[11px] leading-normal" style={{ color: chatCustomization.receiverTextColor }}>
                      Admin reply
                    </p>
                  </div>
                  {/* Metadata */}
                  <div className="flex items-center select-none mb-0.5 min-w-fit">
                    <span className="text-[9px] text-gray-400 font-medium leading-none">
                      12:31
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <button onClick={handleReset} className="w-full mt-4 py-2.5 border border-blue-500 text-blue-600 hover:bg-blue-50 text-sm font-medium rounded-lg transition-all active:scale-95">
              Reset to Default
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // Render the panel into the body using a portal so it's not affected by container transforms
  return createPortal(panelContent, document.body);
};

export default CustomizationPanel;
